var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import DocumentComparisonsApi from '~/api/document_comparison';
import ComparisonApi from '~/api/document_comparisons';
import ProjectDocumentComparisonsApi from '~/api/project-document-comparison';
import ProjectWorkspaceDocumentsApi from '~/api/projects-workspace/documents';
import { updateMetadata } from '~/utils/updateMetadata';
import getComparisons from './actions/get_comparisons';
const ProjectWorkSpaceContext = createContext({});
const ProjectWorkspaceProvider = (props) => {
    const { folderId: activeFolderId } = useParams();
    const [isLoadingComparisonDetail, setIsLoadingComparisonDetail] = useState(false);
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
    const [comparisonDetails, setComparisonDetails] = useState(null);
    const [documents, setDocuments] = useState(null);
    const activeFolderRef = useRef();
    const fetchComparisons = useCallback((projectFolderId) => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoadingComparisonDetail(true);
        setComparisonDetails(null);
        const comparisons = yield getComparisons(projectFolderId);
        if (activeFolderRef.current !== projectFolderId)
            return;
        setComparisonDetails((comparisons === null || comparisons === void 0 ? void 0 : comparisons.length) ? comparisons : null);
        setIsLoadingComparisonDetail(false);
    }), []);
    const fetchDocuments = (projectFolderId) => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoadingDocuments(true);
        setDocuments(null);
        const documents = yield ProjectWorkspaceDocumentsApi.getById(projectFolderId);
        if (activeFolderRef.current !== projectFolderId)
            return;
        const modifiedDocuments = updateMetadata(documents, 'metadata');
        setDocuments((documents === null || documents === void 0 ? void 0 : documents.length) ? modifiedDocuments : null);
        setIsLoadingDocuments(false);
    });
    useEffect(() => {
        if (activeFolderId) {
            activeFolderRef.current = activeFolderId;
            fetchComparisons(activeFolderId);
            fetchDocuments(activeFolderId);
        }
    }, [activeFolderId]);
    const updateComparisonStatus = useCallback((documentComparisonId, status) => {
        if (!documentComparisonId)
            return;
        DocumentComparisonsApi.updateStatus(documentComparisonId, { status });
    }, []);
    const deleteComparisons = useCallback((comparisonIds) => __awaiter(void 0, void 0, void 0, function* () {
        return yield Promise.all(comparisonIds.map(id => ProjectDocumentComparisonsApi.deleteById(id)));
    }), []);
    const updateComparisons = useCallback((comparisons) => __awaiter(void 0, void 0, void 0, function* () {
        return (yield Promise.all(comparisons.map((comparison) => ComparisonApi.update(comparison.documentComparisonId, { isArchived: comparison.isArchived }))));
    }), []);
    const workspaceFolder = {
        comparisonDetails,
        isLoadingDocuments,
        documents,
        setComparisonDetails,
        updateComparisonStatus,
        deleteComparisons,
        updateComparisons,
        activeFolderId,
        isLoadingComparisonDetail,
    };
    return (React.createElement(ProjectWorkSpaceContext.Provider, Object.assign({ value: workspaceFolder }, props)));
};
const useWorkspaceFolder = () => {
    const ctx = useContext(ProjectWorkSpaceContext);
    if (ctx === undefined) {
        throw new Error('WorkspaceFolder can only be used under the WorkspaceFolderProvider');
    }
    return ctx;
};
export { useWorkspaceFolder, ProjectWorkspaceProvider, };
